import axios from 'axios';
import { API_CONFIG, API_ENDPOINTS, ROUTES, STORAGE_KEYS } from '../utils/constants';

// Ensure base URL uses HTTPS in production
const getBaseURL = () => {
  const envURL = process.env.REACT_APP_API_URL;
  if (envURL) {
    // If URL is provided, ensure it uses HTTPS (unless it's localhost)
    if (!envURL.includes('localhost') && !envURL.includes('127.0.0.1') && !envURL.startsWith('https://')) {
      // Only warn in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('Warning: API URL should use HTTPS in production');
      }
    }
    return envURL;
  }
  // Default to localhost (development)
  return 'https://backend.newpays.co/api';
};

const api = axios.create({
  baseURL: getBaseURL(),
  timeout: API_CONFIG.TIMEOUT,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    try {
      const isScreenLocked = typeof window !== 'undefined' && localStorage.getItem('admin_screen_locked') === 'true';
      const isOnAdminPage = typeof window !== 'undefined' && window.location.pathname.includes('/admin/');

      if (isScreenLocked && isOnAdminPage) {
        // Cancel all API requests from admin pages while the admin screen is locked
        // eslint-disable-next-line no-undef
        throw new axios.Cancel('Admin screen is locked');
      }
    } catch {
      // If anything goes wrong with detecting lock state, fall through and allow the request
    }

    // Don't add tokens to auth endpoints (signin, signup, etc.)
    const isAuthEndpoint = 
      config.url?.includes(API_ENDPOINTS.AUTH.SIGNIN) || 
      config.url?.includes(API_ENDPOINTS.AUTH.SIGNUP) ||
      config.url?.includes(API_ENDPOINTS.AUTH.ADMIN_SIGNIN) ||
      config.url?.includes(API_ENDPOINTS.AUTH.FORGOT_PASSWORD) ||
      config.url?.includes(API_ENDPOINTS.AUTH.RESET_PASSWORD);
    
    if (isAuthEndpoint) {
      // Don't add any token to auth endpoints
      return config;
    }
    
    // Determine if this is an admin route by URL or current pathname
    // Exclude auth endpoints from admin route detection
    const isAdminRoute = 
      (config.url?.includes('/admin/') && !config.url?.includes('/auth/')) || 
      config.url?.includes('/live-chat/') ||
      (typeof window !== 'undefined' && window.location.pathname.includes('/admin/') && !window.location.pathname.includes('/admin/login'));
    
    // Get both tokens
    const adminToken = localStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN);
    const userToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
    
    // Priority logic:
    // 1. If we're on an admin page OR making admin/live-chat requests, use adminToken
    // 2. Otherwise, use userToken
    // Always set the token to ensure correct token is used for each request
    if (isAdminRoute) {
      // Admin routes - must use adminToken
      if (adminToken) {
        // Ensure token is trimmed and properly formatted
        const cleanToken = adminToken.trim();
        config.headers.Authorization = `Bearer ${cleanToken}`;
        // Also set it explicitly to ensure it's not overridden
        config.headers['Authorization'] = `Bearer ${cleanToken}`;
        // Store in a custom property to verify it was set
        config._adminTokenUsed = true;
        config._tokenValue = cleanToken;
      } else {
        // If we're on an admin route but no token, this is a problem
        // But don't throw error - let the backend handle it
        console.warn('Admin route but no adminToken available:', config.url);
      }
    } else {
      // Regular routes - use userToken
      if (userToken) {
        config.headers.Authorization = `Bearer ${userToken}`;
      } else if (adminToken) {
        // Fallback: if no userToken but adminToken exists, use adminToken
        config.headers.Authorization = `Bearer ${adminToken}`;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with error
      const { status, data, config } = error.response;
      
      // Don't redirect on 401 for auth endpoints (signin, signup) - let the component handle the error
      const isAuthEndpoint = config?.url?.includes(API_ENDPOINTS.AUTH.SIGNIN) || config?.url?.includes(API_ENDPOINTS.AUTH.SIGNUP);
      
      // Don't redirect on 401 for password change, email update, or 2FA operations - these may return 400/401 for invalid codes
      const isSecurityUpdateEndpoint = 
        config?.url?.includes(API_ENDPOINTS.USER.UPDATE_PASSWORD) || 
        config?.url?.includes(API_ENDPOINTS.USER.UPDATE_EMAIL) ||
        config?.url?.includes('/2fa/');
      
      // NEVER logout on these status codes - they're not authentication failures:
      // 404 = Not Found (resource doesn't exist, not an auth issue)
      // 403 = Forbidden (permission issue, not necessarily auth issue)
      // 400 = Bad Request (client error, not auth issue)
      // 500+ = Server errors (not auth issues)
      const shouldNotLogout = status === 404 || status === 403 || status === 400 || (status >= 500);
      
      // NEVER logout on payment service errors - these are configuration issues, not user auth failures
      const errorMessage = data?.message || '';
      const isPaymentServiceError = errorMessage.includes('Payment service') || errorMessage.includes('payment service');
      
      // Check if the error is a 2FA validation error (not an actual auth failure)
      const is2FAValidationError = 
        errorMessage.includes('2FA') || 
        errorMessage.includes('two-factor') || 
        errorMessage.includes('authenticator') ||
        errorMessage.includes('backup code');
      
      // Only handle 401 (Unauthorized) for logout - this is the only status that indicates auth failure
      // But exclude payment service errors even if they're 401, as they're configuration issues
      // Also exclude security update endpoints (password/email change) - these return 401 for invalid 2FA codes
      if (status === 401 && !isAuthEndpoint && !shouldNotLogout && !isPaymentServiceError && !isSecurityUpdateEndpoint && !is2FAValidationError) {
        // Unauthorized - check if it's an admin route
        const isAdminRoute = 
          (config?.url?.includes('/admin/') && !config?.url?.includes('/auth/')) || 
          config?.url?.includes('/live-chat/') ||
          (typeof window !== 'undefined' && window.location.pathname.includes('/admin/') && !window.location.pathname.includes('/admin/login'));
        
            // Only clear token and redirect if we're sure it's an auth failure
            // Don't redirect if we're already on the login page
            if (isAdminRoute) {
              if (typeof window !== 'undefined' && !window.location.pathname.includes(ROUTES.ADMIN.LOGIN)) {
            // Check if token exists - if it doesn't, it might have been cleared already
            const adminToken = localStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN);
            
            // Check the error message to see if it's a real auth failure
            const errorMessage = data?.message || '';
            const isTokenError = 
              errorMessage.includes('token') || 
              errorMessage.includes('authorization') || 
              errorMessage.includes('unauthorized') ||
              errorMessage.includes('authentication');
            
            // Check if token was actually sent in the request
            const tokenWasSent = config?._adminTokenUsed || config?.headers?.Authorization;
            
            // Only clear token and redirect if:
            // 1. It's a clear token/auth error, OR
            // 2. No token was sent (meaning we should have sent one but didn't)
            if ((isTokenError || !tokenWasSent) && adminToken) {
              console.warn('401 on admin route - clearing token:', {
                url: config?.url,
                errorMessage,
                tokenWasSent: !!tokenWasSent,
                hasToken: !!adminToken
              });
              
              localStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN);
              delete api.defaults.headers.common['Authorization'];
              
              // Use a flag to prevent multiple redirects
              if (!window.__redirectingToAdminLogin) {
                window.__redirectingToAdminLogin = true;
                setTimeout(() => {
                  if (!window.location.pathname.includes(ROUTES.ADMIN.LOGIN)) {
                    window.location.href = ROUTES.ADMIN.LOGIN;
                  }
                  window.__redirectingToAdminLogin = false;
                }, 100);
              }
            } else if (!adminToken) {
              // No token at all - just redirect
              if (!window.__redirectingToAdminLogin) {
                window.__redirectingToAdminLogin = true;
                setTimeout(() => {
                  if (!window.location.pathname.includes(ROUTES.ADMIN.LOGIN)) {
                    window.location.href = ROUTES.ADMIN.LOGIN;
                  }
                  window.__redirectingToAdminLogin = false;
                }, 100);
              }
            } else {
              // Token was sent and it's not a clear auth error - might be a different issue
              // Don't redirect - let the component handle the error
              console.warn('401 on admin route but token was sent and not a clear auth error:', {
                url: config?.url,
                errorMessage,
                tokenWasSent: !!tokenWasSent
              });
            }
          }
        } else {
          // Regular user route
          // Never logout on 404, 403, or other non-auth errors - they're not authentication failures
          // Only logout on 401 (Unauthorized) which indicates token/auth issues
          // Don't redirect if we're on public pages that don't require authentication
          const publicPaths = ['/admin/login', '/reset-password', '/forgot-password', '/verify-email', '/signup', '/signin', '/oauth/callback', '/privacy-policy', '/terms-of-service'];
          const isOnPublicPage = typeof window !== 'undefined' && publicPaths.some(path => window.location.pathname.includes(path));
          if (status === 401 && !shouldNotLogout && typeof window !== 'undefined' && !window.location.pathname.includes(ROUTES.SIGNIN) && !isOnPublicPage) {
            const userToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
            if (userToken) {
              localStorage.removeItem(STORAGE_KEYS.TOKEN);
              delete api.defaults.headers.common['Authorization'];
            }
            if (!window.__redirectingToSignin) {
              window.__redirectingToSignin = true;
              setTimeout(() => {
                const stillOnPublicPage = publicPaths.some(path => window.location.pathname.includes(path));
                if (!window.location.pathname.includes(ROUTES.SIGNIN) && !stillOnPublicPage) {
                  window.location.href = ROUTES.SIGNIN;
                }
                window.__redirectingToSignin = false;
              }, API_CONFIG.REDIRECT_DELAY || 100);
            }
          }
        }
      }
      
    // Return error message from server or default
    // Handle both JSON responses and plain text responses
    if (typeof data === 'string' && data.length > 0) {
      error.message = data;
    } else {
      error.message = data?.message || data?.error || 'An error occurred';
    }
  } else if (error.request) {
    // Request made but no response
    error.message = 'Network error. Please check your connection.';
  } else {
    // Something else happened
    error.message = error.message || 'An unexpected error occurred';
  }
    
    return Promise.reject(error);
  }
);

export default api;
